import React from 'react';
import { Link } from 'react-router-dom';
import blogArticles from '../data/blogArticles';
import JsonLd from '../components/JsonLd';
import { SITE_URL, SITE_NAME } from '../data/seoConfig';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';

const BlogPage = () => {
  const [featured, ...others] = blogArticles;

  const blogJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Conseils événementiels – ' + SITE_NAME,
    url: SITE_URL + '/blog',
    blogPost: blogArticles.map((a) => ({
      '@type': 'BlogPosting',
      headline: a.title,
      description: a.metaDescription,
      url: SITE_URL + '/blog/' + a.slug,
      datePublished: a.dateISO,
      image: a.image,
    })),
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="blog-page">
      <JsonLd data={blogJsonLd} />

      <section className="bg-gradient-to-r from-orange-600 to-orange-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Conseils événementiels</h1>
          <p className="text-base md:text-lg text-orange-100 max-w-2xl mx-auto">
            Dimensions, budget, choix de structure : nos guides pratiques pour réussir votre événement en Normandie.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {featured && (
          <div className="mb-12">
            <Link
              to={'/blog/' + featured.slug}
              className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow"
              data-testid={'blog-card-' + featured.slug}
            >
              <div className="grid md:grid-cols-2">
                <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.imageAlt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-1 text-sm text-orange-500 font-medium mb-3">
                    <Tag className="w-4 h-4" />
                    {featured.category}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-orange-500 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-gray-600 mb-5">{featured.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{featured.date}</span>
                      <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{featured.readingTime}</span>
                    </span>
                    <span className="flex items-center gap-1 text-orange-500 font-medium">
                      Lire l'article <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {others.map((post) => (
            <Link
              key={post.slug}
              to={'/blog/' + post.slug}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow"
              data-testid={'blog-card-' + post.slug}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.image}
                  alt={post.imageAlt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <span className="inline-flex items-center gap-1 text-xs text-orange-500 font-medium mb-2">
                  <Tag className="w-3 h-3" />
                  {post.category}
                </span>
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                  {post.title}
                </h2>
                <p className="text-sm text-gray-500 mb-4">{post.excerpt}</p>
                <span className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{post.date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readingTime}</span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
